import React from "react";
import styled, { keyframes } from "styled-components";
import { device } from "commons/util/helperFunctions";

const HomepageSkeleton = () => {
  return (
    <SkeletonPage aria-hidden="true">
      <HeroSkeleton />
      <SectionSkeleton variant="rail" />
      <SectionSkeleton variant="grid" />
    </SkeletonPage>
  );
};

export const HomepageSectionSkeleton = ({ sectionType, sectionIndex = 0 }) => {
  if (sectionIndex === 0 || sectionType === "BannerCarousel") {
    return <HeroSkeleton compact />;
  }

  if (sectionType === "GridContainer") {
    return <SectionSkeleton variant="grid" compact />;
  }

  return <SectionSkeleton variant="rail" compact />;
};

const HeroSkeleton = ({ compact = false }) => (
  <Hero compact={compact}>
    <HeroCopy>
      <Line width="42%" />
      <Line width="70%" tall />
      <Line width="58%" />
      <ButtonLine />
    </HeroCopy>
    <HeroVisual />
  </Hero>
);

const SectionSkeleton = ({ variant, compact = false }) => (
  <Section compact={compact}>
    <Line width="190px" />
    <Cards variant={variant}>
      {Array.from({ length: variant === "grid" ? 6 : 4 }).map((_, index) => (
        <Card key={index} variant={variant}>
          <Media variant={variant} />
          <Line width="70%" />
          <Line width="45%" />
        </Card>
      ))}
    </Cards>
  </Section>
);

const pulse = keyframes`
  0%, 100% {
    opacity: 0.52;
  }
  50% {
    opacity: 0.88;
  }
`;

const BaseBlock = styled.div`
  background: linear-gradient(90deg, #eef2f6 0%, #f7f9fb 48%, #eef2f6 100%);
  animation: ${pulse} 1.6s ease-in-out infinite;
`;

const SkeletonPage = styled.div`
  width: 100%;
  background: #ffffff;
  margin-top: -15px;
`;

const Hero = styled.section`
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-height: ${({ compact }) => (compact ? "420px" : "520px")};
  padding: 32px 24px;
  background: #f3f7fa;

  @media ${device.tablet}, ${device.laptop} {
    flex-direction: row;
    align-items: center;
    min-height: ${({ compact }) => (compact ? "440px" : "560px")};
    padding: 56px 10vw;
  }
`;

const HeroCopy = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  gap: 18px;
  min-width: 0;
`;

const HeroVisual = styled(BaseBlock)`
  flex: 1;
  width: 100%;
  min-height: 260px;
  border-radius: 8px;

  @media ${device.tablet}, ${device.laptop} {
    min-height: 360px;
  }
`;

const Section = styled.section`
  padding: ${({ compact }) => (compact ? "24px 0" : "48px 24px")};

  @media ${device.laptop} {
    padding: ${({ compact }) => (compact ? "32px 0" : "56px 9vw")};
  }
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: ${({ variant }) =>
    variant === "grid" ? "repeat(2, minmax(0, 1fr))" : "repeat(4, 180px)"};
  gap: 18px;
  overflow: hidden;
  margin-top: 22px;

  @media ${device.laptop} {
    grid-template-columns: ${({ variant }) =>
      variant === "grid"
        ? "repeat(3, minmax(0, 1fr))"
        : "repeat(4, minmax(0, 1fr))"};
    gap: 24px;
  }
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`;

const Media = styled(BaseBlock)`
  width: 100%;
  min-height: ${({ variant }) => (variant === "grid" ? "180px" : "132px")};
  border-radius: 8px;

  @media ${device.laptop} {
    min-height: ${({ variant }) => (variant === "grid" ? "220px" : "168px")};
  }
`;

const Line = styled(BaseBlock)`
  width: ${({ width }) => width || "100%"};
  height: ${({ tall }) => (tall ? "34px" : "16px")};
  max-width: 100%;
  border-radius: 999px;
`;

const ButtonLine = styled(BaseBlock)`
  width: 150px;
  height: 48px;
  border-radius: 999px;
  margin-top: 8px;
`;

export default HomepageSkeleton;
